import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Megaphone, Users } from "lucide-react";

/**
 * Admin card that lets a compliance/ops user broadcast a push notification
 * to all opted-in traders, optionally filtered by tier. Two-step UX:
 *   1) Preview recipient count (auto-refreshes when tier_filter changes)
 *   2) Confirm & Send button — fires the broadcast + refreshes history
 */
export default function AdminBroadcastCard() {
    const [title, setTitle] = useState("");
    const [body,  setBody]  = useState("");
    const [url,   setUrl]   = useState("/app");
    const [tier,  setTier]  = useState("all");
    const [count, setCount] = useState(null);
    const [busy,  setBusy]  = useState(false);
    const [history, setHistory] = useState([]);

    async function refreshPreview(t = tier) {
        try {
            const { data } = await api.get(`/push/broadcast/preview?tier_filter=${t}`);
            setCount(data.recipient_count);
        } catch {
            setCount(null);
        }
    }

    async function loadHistory() {
        try {
            const { data } = await api.get("/push/broadcast/history?limit=10");
            setHistory(data);
        } catch { /* silent */ }
    }

    useEffect(() => { refreshPreview("all"); loadHistory(); }, []);
    useEffect(() => { refreshPreview(tier); }, [tier]);

    async function send() {
        if (!title.trim() || !body.trim()) {
            toast.error("Title and body are required.");
            return;
        }
        if (count === 0) {
            toast.error("Nobody matches this filter — nothing to send.");
            return;
        }
        if (!window.confirm(`Send push to ${count} ${tier === "all" ? "opted-in users" : `${tier}-tier users`}?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/push/broadcast", {
                title: title.trim(), body: body.trim(), url: url.trim() || "/app", tier_filter: tier,
            });
            toast.success(`📣 Broadcast queued to ${data.recipient_count} recipients.`);
            setTitle(""); setBody("");
            await loadHistory();
            await refreshPreview(tier);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send broadcast — please try again.");
        } finally { setBusy(false); }
    }

    return (
        <div className="border border-border bg-card rounded p-4 space-y-4" data-testid="admin-broadcast-card">
            <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-primary" />
                <div className="text-lg font-bold tracking-tight">Broadcast</div>
                <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    <span data-testid="broadcast-recipient-count">{count === null ? "…" : count}</span> recipients
                </span>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <Label htmlFor="bc-title" className="text-xs">Title</Label>
                    <Input
                        id="bc-title" maxLength={80}
                        placeholder="NSE closes early tomorrow"
                        value={title} onChange={(e) => setTitle(e.target.value)}
                        data-testid="broadcast-title"
                    />
                </div>
                <div>
                    <Label htmlFor="bc-tier" className="text-xs">Send to</Label>
                    <Select value={tier} onValueChange={setTier}>
                        <SelectTrigger id="bc-tier" data-testid="broadcast-tier"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All opted-in users</SelectItem>
                            <SelectItem value="individual">Individual (free) only</SelectItem>
                            <SelectItem value="moe_institution">MoE Institution only</SelectItem>
                            <SelectItem value="cma_institution">CMA Institution only</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div>
                <Label htmlFor="bc-body" className="text-xs">Message body</Label>
                <Textarea
                    id="bc-body" maxLength={240} rows={3}
                    placeholder="Trading window closes at 12:30 EAT for the public holiday. Wrap open positions early."
                    value={body} onChange={(e) => setBody(e.target.value)}
                    data-testid="broadcast-body"
                />
                <div className="text-[10px] text-muted-foreground text-right mt-0.5">{body.length}/240</div>
            </div>

            <div>
                <Label htmlFor="bc-url" className="text-xs">Deep-link URL (optional)</Label>
                <Input
                    id="bc-url" maxLength={200}
                    placeholder="/app/terminal"
                    value={url} onChange={(e) => setUrl(e.target.value)}
                    data-testid="broadcast-url"
                />
            </div>

            <Button
                onClick={send} disabled={busy || !title.trim() || !body.trim() || count === 0}
                className="w-full gap-1.5" data-testid="broadcast-send"
            >
                <Send className="w-4 h-4" />
                {busy ? "Sending…" : `Send to ${count ?? "…"} ${tier === "all" ? "users" : tier}`}
            </Button>

            {history.length > 0 && (
                <div className="pt-3 border-t border-border">
                    <div className="overline mb-2">Recent broadcasts</div>
                    <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="broadcast-history">
                        {history.map((h) => (
                            <div key={h.id} className="text-xs flex items-start gap-2 border border-border/50 rounded p-2">
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold truncate">{h.title}</div>
                                    <div className="text-muted-foreground truncate">{h.body}</div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                        {h.recipient_count} recipient{h.recipient_count === 1 ? "" : "s"} · {h.tier_filter} · by {h.sent_by_email}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
